import { Button } from '@1trading/react';
import { useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Plus, Trash2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { DataTable } from '@/components/data-table/data-table';
import { confirmAction } from '@/components/dialogs/confirm-action';
import { FEE_EXCLUSIONS_QUERY_KEY } from '@/hooks/queries/fees/use-fees';
import { deleteFeeExclusion, type FeeExclusion } from '@/services/api/fees';
import { FeeExclusionDialog } from './fee-exclusion-dialog';
import { FeesErrorBanner } from './fees-error-banner';

const EMPTY_MESSAGE = 'No fee exclusions data available';

export interface FeeExclusionsPanelProps {
  exclusions: FeeExclusion[] | undefined;
  isLoading: boolean;
  errorMessage: string | null;
  onRetry: () => void;
}

/** Exclusions tab: add button, pid/name table, delete flow. */
export const FeeExclusionsPanel = ({ exclusions, isLoading, errorMessage, onRetry }: FeeExclusionsPanelProps) => {
  const queryClient = useQueryClient();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  // Legacy rendered
  // this trash button disabled with no route behind it, but DELETE /v1/fees/exclusions
  // exists and works upstream. Fall back to disabling it again if ops object.
  const handleDelete = useCallback(
    async (exclusion: FeeExclusion) => {
      const justification = await confirmAction({
        title: 'Confirm Delete fee exclusion',
        description: `Are you sure you want to delete the fee exclusion for ${exclusion.pid} (${exclusion.name})? This action cannot be undone.`,
        actionLabel: 'Delete fee exclusion',
        destructive: true,
        // Asymmetry: the list returns `pid`; the delete sends `accountHolderId`
        onConfirm: (comment) => deleteFeeExclusion({ accountHolderId: exclusion.pid, comment }),
      });
      if (justification === null) return;

      // deleteFeeExclusion can 200 even after an internal failure —
      // "submitted" over "deleted", and refetch so the operator verifies the removal.
      toast.success(`Delete submitted for fee exclusion ${exclusion.name} — verify it left the list.`);
      await queryClient.invalidateQueries({ queryKey: FEE_EXCLUSIONS_QUERY_KEY });
    },
    [queryClient],
  );

  const columns = useMemo(
    (): ColumnDef<FeeExclusion, unknown>[] => [
      {
        accessorKey: 'pid',
        header: 'Account Holder ID',
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.pid}</span>,
      },
      { accessorKey: 'name', header: 'Name' },
      {
        id: 'actions',
        enableSorting: false,
        header: '',
        cell: ({ row }) => (
          <Button aria-label={`Delete fee exclusion for ${row.original.name}`} onClick={() => void handleDelete(row.original)} size="icon" type="button" variant="ghost">
            <Trash2 className="size-4" />
          </Button>
        ),
      },
    ],
    [handleDelete],
  );

  return (
    <div className="flex max-w-[800px] flex-col gap-3">
      <div>
        <Button className="gap-2" onClick={() => setIsAddDialogOpen(true)} type="button">
          <Plus className="size-4" />
          Add Fee Exclusion
        </Button>
      </div>

      {errorMessage ? (
        <FeesErrorBanner message={errorMessage} onRetry={onRetry} />
      ) : (
        <DataTable columns={columns} data={exclusions} emptyMessage={EMPTY_MESSAGE} getRowId={(row) => row.pid} isLoading={isLoading} />
      )}

      {isAddDialogOpen ? <FeeExclusionDialog onClose={() => setIsAddDialogOpen(false)} /> : null}
    </div>
  );
};
FeeExclusionsPanel.displayName = 'FeeExclusionsPanel';
